// What an edit to an existing ticket actually sends.
//
// Pure, and separate from the panel, for this repository's standing reason: there is no
// DOM harness, so a decision left inside a component is a decision nothing can check.
//
// THE RISK IS THE SAME ONE `customer-edit.js` GUARDS, and it is worse here because the
// ticket form has more fields. `PATCH /api/tickets/:publicRef` treats an absent key as
// "leave it alone" and a present one as an instruction, so a panel that posts its whole
// state on every save turns "I added a tag" into a write of the queue, the assignee and
// the requester too. On this endpoint that is not merely noisy: `assigned_user_id`
// re-runs `assertAssignable`, `customer_id` re-runs the CC check, and each written
// field writes an audit row — so a one-tag edit would leave a history nobody made and
// could be refused for a field the agent never touched.
//
// A LIST IS EQUAL WHEN ITS CONTENTS ARE. Tags, CCs and followers are replacements, so
// comparing them by reference or by index would report an unchanged list as changed
// every time the panel re-rendered — and the server, seeing the key present, would
// diff it to nothing and write nothing, which is silent but costs a round trip and
// makes the no-op assertions elsewhere meaningless.

/** Scalars the panel may write. `status_id` rides with `status` — see below. */
const SCALARS = ['status', 'priority', 'type', 'assigned_user_id', 'queue_id', 'customer_id'];

/** Replacements, compared as SETS. */
const LISTS = ['tags', 'cc_customer_ids', 'follower_user_ids'];

/** `null`, `undefined` and `''` are one value in a form; `0` is not. */
const same = (a, b) => String(a ?? '') === String(b ?? '');

const sameSet = (a, b) => {
  const left = [...new Set((a ?? []).map(String))].sort();
  const right = [...new Set((b ?? []).map(String))].sort();
  return left.length === right.length && left.every((v, i) => v === right[i]);
};

/**
 * Only what changed, as the PATCH body. Empty means "do not send".
 *
 * `''` becomes `null` for the id fields, because a cleared picker means "unassign" or
 * "take it out of the queue" and the API tells that from "leave it alone" by whether
 * the key is present. `customer_id` is the exception: the endpoint refuses null for it
 * — a ticket always has a requester — so an empty value is dropped rather than sent as
 * a null the server would reject.
 *
 * @param {object} before  The ticket as loaded, flattened by `draftFromTicket`.
 * @param {object} draft   The panel's current state.
 */
export function changedTicketFields(before, draft) {
  const out = {};

  for (const field of SCALARS) {
    if (same(before?.[field], draft?.[field])) continue;
    const value = draft?.[field];
    if (field === 'customer_id' && (value === '' || value == null)) continue;
    out[field] = value === '' || value === undefined ? null : value;
  }

  for (const field of LISTS) {
    if (sameSet(before?.[field], draft?.[field])) continue;
    out[field] = [...new Set((draft?.[field] ?? []).map(String))];
  }

  return out;
}

/**
 * The ticket's editable fields, flattened out of the §3.2 detail shape.
 *
 * One function so the "before" and the panel's initial state cannot disagree: they are
 * the same object. A hand-built initial state that read `ticket.queue_id` (which the
 * detail shape does not have — it nests `queue.id`) would come back undefined and make
 * every save look like a queue change.
 */
export function draftFromTicket(ticket) {
  return {
    status: ticket?.status ?? '',
    priority: ticket?.priority ?? '',
    type: ticket?.type ?? '',
    assigned_user_id: ticket?.assignee?.id ? String(ticket.assignee.id) : '',
    queue_id: ticket?.queue?.id ? String(ticket.queue.id) : '',
    customer_id: ticket?.customer?.id ? String(ticket.customer.id) : '',
    tags: [...(ticket?.tags ?? [])],
    cc_customer_ids: (ticket?.ccs ?? []).map((cc) => String(cc.customer.id)),
    follower_user_ids: (ticket?.followers ?? []).map((f) => String(f.user.id)),
  };
}

/**
 * A one-line summary of what is about to be saved, for the button.
 *
 * Not a confirmation — none of these needs one. It exists because a Save button that
 * looks identical whether it will write one field or six invites the click that writes
 * six, and this panel deliberately has no dialog in front of it.
 */
export function describeTicketChanges(patch) {
  const labels = {
    status: 'status',
    priority: 'priority',
    type: 'type',
    assigned_user_id: 'assignee',
    queue_id: 'queue',
    customer_id: 'requester',
    tags: 'tags',
    cc_customer_ids: 'CCs',
    follower_user_ids: 'followers',
  };
  const named = Object.keys(patch ?? {}).map((key) => labels[key] ?? key);
  if (named.length === 0) return null;
  if (named.length === 1) return `Save ${named[0]}`;
  if (named.length === 2) return `Save ${named[0]} and ${named[1]}`;
  return `Save ${named.length} changes`;
}
